/**
 * Supplier reliability scoring — fill rate + late rate model.
 *
 * Derives supplier reliability from their completed PO execution record.
 * Classification is based on two authoritative metrics:
 *
 *   fillRate  = receivedUnits / orderedUnits across all active POs
 *   lateRate  = latePOs / completedPOs (received late / all received)
 *
 * Both are derived entirely from authoritative PO state and date fields:
 *   status, expectedDeliveryDate, actualDeliveryDate, products[].quantity
 *
 * Evaluation order:
 *   1. insufficient-data  — completedPOs < 3 (no certifiable history)
 *   2. unreliable         — fillRate < 70% OR lateRate > 40%
 *   3. degraded           — fillRate < 85% OR lateRate > 20%
 *   4. marginal           — fillRate < 95% OR lateRate > 10%
 *   5. reliable           — fillRate >= 95% AND lateRate <= 10%
 *
 * derive, never store — computed at read time, no background jobs.
 *
 * defectRate and rejectionCount are included in the metrics shape as
 * zeroed placeholders; the PO model does not yet capture those fields.
 *
 * @param {object}   _supplier         — Supplier document (reserved for future signals)
 * @param {object[]} posForSupplier    — POs already filtered to this supplier
 * @returns {{ level, label, sub, badgeClass, icon, metrics }}
 */

/** PO statuses representing no delivery commitment */
const INACTIVE = new Set(['draft', 'cancelled']);

/** Minimum completed (received) POs required to certify reliability */
const MIN_COMPLETED = 3;

const MS_PER_DAY = 86_400_000;

/** Sum product quantities across a set of POs */
function sumUnits(pos) {
  return pos.reduce(
    (total, po) =>
      total + (po.products ?? []).reduce((s, item) => s + (item.quantity ?? 0), 0),
    0,
  );
}

export function computeSupplierRisk(_supplier, posForSupplier) {
  const allPos = Array.isArray(posForSupplier) ? posForSupplier : [];
  const activePOs = allPos.filter(po => !INACTIVE.has(po.status));
  const completedPOs = activePOs.filter(po => po.status === 'received');

  // Guard: insufficient history
  if (completedPOs.length < MIN_COMPLETED) {
    const count = completedPOs.length;
    return {
      level: 'insufficient-data',
      label: 'Insufficient History',
      sub: count === 0
        ? 'No completed POs'
        : `${count} of ${MIN_COMPLETED} POs completed`,
      badgeClass: 'bg-gray-100 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400',
      icon: 'HelpCircle',
      metrics: {
        completedPOs: count,
        latePOs: 0,
        lateRate: 0,
        orderedUnits: sumUnits(activePOs),
        receivedUnits: sumUnits(completedPOs),
        fillRate: 0,
        defectRate: 0,
        rejectionCount: 0,
        avgDaysLate: 0,
      },
    };
  }

  // Metrics
  const latePOs = completedPOs.filter(po => {
    if (!po.expectedDeliveryDate || !po.actualDeliveryDate) return false;
    return new Date(po.actualDeliveryDate).getTime() > new Date(po.expectedDeliveryDate).getTime();
  });

  const lateRate = latePOs.length / completedPOs.length;

  const orderedUnits = sumUnits(activePOs);
  const receivedUnits = sumUnits(completedPOs);
  const fillRate = orderedUnits > 0 ? receivedUnits / orderedUnits : 1.0;

  let avgDaysLate = 0;
  if (latePOs.length > 0) {
    const total = latePOs.reduce((sum, po) => {
      const d =
        (new Date(po.actualDeliveryDate).getTime() -
          new Date(po.expectedDeliveryDate).getTime()) /
        MS_PER_DAY;
      return sum + Math.max(0, d);
    }, 0);
    avgDaysLate = Math.round(total / latePOs.length);
  }

  const metrics = {
    completedPOs: completedPOs.length,
    latePOs: latePOs.length,
    lateRate: Math.round(lateRate * 100),
    orderedUnits,
    receivedUnits,
    fillRate: Math.round(fillRate * 100),
    defectRate: 0,
    rejectionCount: 0,
    avgDaysLate,
  };

  // Classification (worst first)

  // unreliable: fill rate < 70% OR late rate > 40%
  if (fillRate < 0.70 || lateRate > 0.40) {
    const sub =
      fillRate < 0.70
        ? `${metrics.fillRate}% fill rate`
        : `${metrics.lateRate}% late delivery rate`;
    return {
      level: 'unreliable', label: 'Unreliable', sub,
      badgeClass: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
      icon: 'AlertTriangle', metrics,
    };
  }

  // degraded: fill rate 70-85% OR late rate 20-40%
  if (fillRate < 0.85 || lateRate > 0.20) {
    const sub =
      lateRate > 0.20
        ? `${metrics.lateRate}% late delivery rate`
        : `${metrics.fillRate}% fill rate`;
    return {
      level: 'degraded', label: 'Degraded', sub,
      badgeClass: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
      icon: 'TrendingDown', metrics,
    };
  }

  // marginal: fill rate 85-95% OR late rate 10-20% (10% inclusive per spec)
  if (fillRate <= 0.95 || lateRate >= 0.10) {
    const sub =
      lateRate >= 0.10
        ? `${metrics.lateRate}% late delivery rate`
        : `${metrics.fillRate}% fill rate`;
    return {
      level: 'marginal', label: 'Marginal', sub,
      badgeClass: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
      icon: 'Clock', metrics,
    };
  }

  // reliable: fill rate >= 95% AND late rate <= 10%
  const onTimeCount = completedPOs.length - latePOs.length;
  const sub =
    onTimeCount === completedPOs.length
      ? `${completedPOs.length} PO${completedPOs.length !== 1 ? 's' : ''} on time`
      : `${onTimeCount} of ${completedPOs.length} POs on time`;
  return {
    level: 'reliable', label: 'Reliable', sub,
    badgeClass: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    icon: 'CheckCircle', metrics,
  };
}

/** Sort weight — lower number = higher urgency */
export const SUPPLIER_RISK_SORT_ORDER = {
  'unreliable':        0,
  'degraded':          1,
  'marginal':          2,
  'insufficient-data': 3,
  'reliable':          4,
};
